import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Box, Typography, Divider, Button } from '@mui/material';
import { keyframes } from '@mui/system';
import LinkIcon from '@mui/icons-material/Link';
import normalizeAiMarkdown from '../../utils/aiMarkdown';
import 'katex/dist/katex.min.css';

const ACTION_BUTTON_TEXT_PATTERN = /^(go to|go to page|schedule showing|upload documents|start negotiation|take action)$/i;

const ellipsis = keyframes`
  0% { content: '.'; }
  33% { content: '..'; }
  66% { content: '...'; }
  100% { content: '.'; }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const getChildrenText = (children) => {
  if (typeof children === 'string') return children;
  if (!Array.isArray(children)) return String(children || '');

  return children
    .map((child) => {
      if (typeof child === 'string') return child;
      if (child && typeof child === 'object' && typeof child.props?.children === 'string') {
        return child.props.children;
      }
      return '';
    })
    .join('');
};

const buildMarkdownComponents = ({
  isPanel,
  isThinking,
  isError,
  renderActionLinksAsButtons,
}) => ({
  p: ({ children }) => (
    <Typography
      component="p"
      variant={isPanel ? 'body2' : 'body1'}
      sx={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        fontSize: isPanel ? '0.875rem' : undefined,
        my: 0,
        lineHeight: 1.55,
        ...(isThinking && {
          background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 25%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.3) 75%)',
          backgroundSize: '200% 100%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          animation: `${shimmer} 2s infinite linear`,
          fontStyle: 'italic',
          display: 'inline-block',
          position: 'relative',
          '&::after': {
            content: '"..."',
            position: 'absolute',
            right: '-1.5em',
            animation: `${ellipsis} 1s steps(4, end) infinite`,
          },
        }),
        ...(isError && {
          color: '#f34f4f',
          fontWeight: 500,
        }),
      }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => <Typography variant="h5" component="h1" sx={{ mt: 1.2, mb: 0.6 }}>{children}</Typography>,
  h2: ({ children }) => <Typography variant="h6" component="h2" sx={{ mt: 1.1, mb: 0.6 }}>{children}</Typography>,
  h3: ({ children }) => <Typography variant="subtitle1" component="h3" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>{children}</Typography>,
  h4: ({ children }) => <Typography variant="subtitle1" component="h4" sx={{ mt: 0.9, mb: 0.5, fontWeight: 600 }}>{children}</Typography>,
  h5: ({ children }) => <Typography variant="body1" component="h5" sx={{ mt: 0.8, mb: 0.4, fontWeight: 600 }}>{children}</Typography>,
  h6: ({ children }) => <Typography variant="body2" component="h6" sx={{ mt: 0.8, mb: 0.4, fontWeight: 600 }}>{children}</Typography>,
  strong: ({ children }) => (
    <Typography component="strong" sx={{ fontWeight: 600, color: 'primary.main' }}>
      {children}
    </Typography>
  ),
  em: ({ children }) => (
    <Typography component="em" sx={{ fontStyle: 'italic', color: 'primary.light' }}>
      {children}
    </Typography>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderRadius: 1,
        p: 1.25,
        overflowX: 'auto',
        my: 0.8,
        fontSize: isPanel ? '0.75rem' : '0.85rem',
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Typography
      component="code"
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        padding: '2px 4px',
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.875em',
      }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, my: 0.75 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, my: 0.75 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box
      component="li"
      sx={{
        mb: 0.4,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        fontSize: isPanel ? '0.875rem' : '1rem',
        lineHeight: 1.5,
        '& > p': { margin: 0 },
        '& > div': { margin: 0 },
        '& > ul, & > ol': { mt: 0.5, mb: 0 },
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 360, width: '100%' }}>
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }) => <Box component="thead" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>{children}</Box>,
  tbody: ({ children }) => <Box component="tbody">{children}</Box>,
  tr: ({ children }) => <Box component="tr">{children}</Box>,
  th: ({ children }) => (
    <Box component="th" sx={{ border: '1px solid rgba(0,0,0,0.12)', p: 0.75, textAlign: 'left', fontWeight: 600 }}>
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box component="td" sx={{ border: '1px solid rgba(0,0,0,0.12)', p: 0.75, verticalAlign: 'top' }}>
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        pl: 2,
        borderLeft: '4px solid rgba(0, 0, 0, 0.15)',
        color: 'rgba(0, 0, 0, 0.65)',
        fontStyle: 'italic',
        my: 1,
        mx: 0,
      }}
    >
      {children}
    </Box>
  ),
  hr: () => <Divider sx={{ my: 1.2 }} />,
  a: ({ children, href }) => {
    const childrenText = getChildrenText(children).trim();
    const isActionButton = renderActionLinksAsButtons && ACTION_BUTTON_TEXT_PATTERN.test(childrenText);

    if (isActionButton && href) {
      return (
        <Box component="span" sx={{ display: 'inline-block', mt: 0.8, mb: 0.8 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<LinkIcon />}
            sx={{
              padding: isPanel ? '8px 16px' : '10px 22px',
              fontSize: isPanel ? '0.75rem' : '0.875rem',
              backgroundColor: '#1976d2',
              color: '#fff',
              '&:hover': { backgroundColor: '#115293' },
            }}
            onClick={(event) => {
              event.preventDefault();
              if (href.startsWith('/')) {
                window.location.href = href;
                return;
              }
              window.open(href, '_blank', 'noopener,noreferrer');
            }}
          >
            {children}
          </Button>
        </Box>
      );
    }

    if (!href) {
      return <>{children}</>;
    }

    return (
      <Typography
        component="a"
        href={href}
        target={href.startsWith('http') ? '_blank' : '_self'}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          '&:hover': { color: 'primary.dark' },
          fontSize: 'inherit',
        }}
      >
        {children}
      </Typography>
    );
  },
  img: ({ src, alt }) => (
    <Box
      component="img"
      src={src}
      alt={alt || 'Markdown image'}
      sx={{ maxWidth: '100%', borderRadius: 1, my: 1 }}
      loading="lazy"
    />
  ),
});

const AiMarkdownRenderer = ({
  content,
  isPanel = false,
  isThinking = false,
  isError = false,
  renderActionLinksAsButtons = false,
  sx = {},
}) => {
  const normalizedContent = React.useMemo(() => normalizeAiMarkdown(content), [content]);

  const markdownComponents = React.useMemo(() => buildMarkdownComponents({
    isPanel,
    isThinking,
    isError,
    renderActionLinksAsButtons,
  }), [isPanel, isThinking, isError, renderActionLinksAsButtons]);

  if (!normalizedContent) {
    return null;
  }

  return (
    <Box
      sx={{
        '& > p + p': { mt: 1 },
        '& > p + ul, & > p + ol': { mt: 0.75 },
        '& > ul + p, & > ol + p': { mt: 0.75 },
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        ...sx,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={markdownComponents}
        skipHtml
      >
        {normalizedContent}
      </ReactMarkdown>
    </Box>
  );
};

export default AiMarkdownRenderer;
